import axios from 'axios';
import { message, Modal } from 'antd';
import moment from 'moment';
import qs from 'querystring';

import { getBaseUrl, isProd } from 'src/utils';
import objectToForm from '../utils/objectToForm';
import { getDateFormat } from './constants';
import { isMC } from '../utils/env';
import { version } from '../utils/version';
import { $t, download } from '../tools';
import { removeStorage, STORAGE } from '../tools/storage';
import { callNativeAsyncFunc, fromNpNative, fromNpWeb } from '../native';


const format = 'YYYY-MM-DD';
const codeMessage = {
  200: '服务器成功返回请求的数据',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）',
  204: '删除数据成功。',
  400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
  401: 'token已失效, 请重新登录',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '发出的请求针对的是不存在的记录，服务器没有进行操作',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  500: '服务器发生错误，请检查服务器',
  502: '网关错误',
  503: '服务不可用，服务器暂时过载或维护',
  504: '网关超时',
};

export default (store, persistor) => {
  // 1 域名及token处理
  // 1.1 设置默认域名(origin)
  axios.defaults.baseURL = getBaseUrl();

  axios.interceptors.request.use(
    (config) => {
      // 1.2 非默认域名设置 axiosConfig
      // axiosConfig 只有在请求需要做特殊处理的时候使用，与接口参数本身无关
      const { axiosConfig, ...params } = config.params || {};

      // axiosConfig 请求存在于config
      // e.g. axios.get('', { axiosConfig: { key: 'databox' } });
      // e.g. axios.post('', {}, { axiosConfig: { key: 'native' } });
      const conf = config.axiosConfig || axiosConfig;

      // 1.3 非默认域名则替换为所需域名
      if ((conf?.key && conf.key !== 'origin') || conf?.env) {
        config.baseURL = getBaseUrl(conf.key, conf.env);
      }

      // 为接口添加 token
      const state = store.getState();
      const { token, locale, maskToken,backToken,backMaskToken } = state.appState;

      if (
        !isProd() &&
        !config.url.startsWith('https://translate2.dokkr.net') &&
        !config.url.startsWith('https://translate.duoke.net')
      ) {
        // 1.4 除翻译链接外都带 maskToken
        if (!isMC()) {
          if (maskToken) {
            config.headers.Authorization = maskToken;
          }
          config.headers['gm-app-pid'] = isMC() ? 142 : 132;
          config.headers['gm-app-version'] = version;
        }
      }
      if (conf?.hasKey) {
        params.key = token;
      }
      if (!conf?.key || conf?.key === 'origin') {
        params.key = token;
        params.version = version;
      } else if (isProd()) {
        if (maskToken) {
          config.headers.Authorization = maskToken;
        }
        config.headers['gm-app-pid'] = isMC() ? 142 : 132;
        config.headers['gm-app-version'] = version;
      }
      // 判断是否来自网页 newPro
      // if((fromNpWeb || fromNpNative)&&backToken && backMaskToken){
      //   params.key = backToken
      //   config.headers.Authorization = backMaskToken;
      // }

      // 2 如果需要返回 config
      if (config.needResponse || conf?.needResponse) {
        params.needResponse = 1;
      }

      // 3 MC 的逻辑
      if (isMC()) {
        params.pid = 5;
        params.lang = params.lang || locale;
        params.version = version;
      }
      // 4 值为空时不传，日期换为统一格式请求
      for (const key in params) {
        if (['sday', 'eday'].includes(key) && params[key] && !/^[0-9-]+$/.test(params[key])) {
          params[key] = moment(params[key], getDateFormat(locale)).format(format);
        }
        if (!params[key] && params[key] !== 0) {
          delete params[key];
        }
      }
      if (config.data && (!conf?.key || conf?.key === 'origin')) {
        for (const key in config.data) {
          if (
            ['sday', 'eday'].includes(key) &&
            config.data[key] &&
            !/^[0-9-]+$/.test(config.data[key])
          ) {
            config.data[key] = moment(config.data[key], getDateFormat(locale)).format(format);
          }
        }
        config.data = objectToForm(config.data);
      }

      config.params = params;
      if (config.method === 'get' && config.type === 'download') {
        download(`${config.baseURL}${config.url}?${qs.stringify(params)}`, { isUrl: true });
        return false;
      }

      return config;
    },
    (error) => {
      console.log(error);
      return null;
    }
  );

  const responseCondition = new Map([
    [
      // token 失效
      [6, 6061, 6011],
      async (config) => {
        await persistor.purge()
        if(fromNpWeb || fromNpNative){
          callNativeAsyncFunc({ ns: 'airWeb', fn: 'loginLost' }).then (r =>{});
        }else{
          await store.dispatch({ type: 'app/setState', payload: { token: '', maskToken: '' } })
        }
        if (window.location.hash === '#/login') return null;
        else window.location.href = '#/login';
      },
    ],
    [
      // 30007 报表未授权; 30010 不显示报表入口
      [30007, 30010],
      (config) => config.data,
    ],
  ]);

  const getMapValue = (key) => {
    if (responseCondition.has(key)) return responseCondition.get(key);
    else {
      for (let [k, v] of responseCondition) {
        if (k instanceof RegExp && k.test(key)) return v;
        if (k instanceof Array && k.includes(key)) return v;
      }
    }
    return null;
  };

  axios.interceptors.response.use(
    (config) => {
      if (config.config?.responseType === 'blob') {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsText(config.data);
          reader.onload = (e) => {
            if (e.target?.result?.includes('"err"')) {
              const res = JSON.parse(e.target?.result || '{}');
              if (Number(res?.err || 0) === 1008) {
                // if (!isMC()) {
                Modal.info({
                  title: $t('tip', 'Import'),
                  content: $t('upload_tip34', 'Import'),
                  okText: $t('good', 'Import'),
                });
              } else {
                Modal.info({
                  title: $t('tip', 'Import'),
                  content: res?.msg || '数据为空，导出失败',
                  okText: $t('good', 'Import'),
                });
              }
              resolve(null);
            } else {
              resolve(config.data);
            }
          };
        });
      } else {
        const code = Number(config.data.err || config.data.code);
        if (!code || config.data.duplicate_name || (config.data.list && config.data.list.length)) {
          return config.data;
        }
        const func = getMapValue(code);
        if (func) {
          return func(config);
        }
        if(config?.url?.includes('api/data/get')){
          return config.data;
        }
        message.warn(
          config.data?.info ||
            config.data?.msg ||
            config.data?.message ||
            $t('upload_tip11', 'Import')
        );
        return config.data;
      }
    },
    (error) => {
      const { config, response } = error;
      const msg = isMC() ? '' : codeMessage[response?.status];
      if (!!msg) {
        if (config.config?.params?.needResponse) {
          return { err: -1, msg: `${response.status}: ${msg}` };
        } else {
          message.warn(msg);
        }
      }
      return null;
    }
  );
};
